import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Spin, Typography, Button, theme } from 'antd';
import {
  IconSearch,
  IconMap2,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconCurrentLocation,
  IconLayersIntersect,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { message } from '../services/toastMessage';
import { getMapsToken } from '../services/MapsService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL } from '../theme/creamyTokens';

const { Text } = Typography;

export interface IMapBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export interface SpacesMapHandle {
  /** The map's current viewport, read live -- for a caller that needs "what
   * the guest is looking at right now" on demand (e.g. HeavyDutyMachineries'
   * search-with-no-location-typed case), rather than waiting for the next
   * moveend the way onBoundsChange does. Undefined before the map's ready
   * (or if maps aren't configured on this environment at all). */
  getBounds: () => IMapBounds | undefined;
}

/**
 * The minimal shape SpacesMap needs to plot one pin -- originally just
 * IHeavyDutyMachinery's own fields read directly, generalized so the same
 * map can plot Operators/Accessories too (see OperatorSearchResult/
 * AccessorySearchResult's own machineryLatitude/machineryLongitude, which
 * is where THEIR pins live -- see those interfaces' own doc comments for
 * why). priceLabel/liveStatus are precomputed by the caller (see
 * machineryMapPin.ts's own toMachineryMapPin) rather than derived in here,
 * since how to price/status-label an Operator or Accessory is category-
 * specific knowledge this map component has no business knowing.
 */
export interface IMapPin {
  id: string;
  latitude?: number;
  longitude?: number;
  priceLabel: string;
  liveStatus?: string;
}

interface SpacesMapProps {
  items: IMapPin[];
  /**
   * Fired only when the user explicitly confirms the "Search this area"
   * button, with whatever viewport is current at that moment -- not on
   * every pan/zoom (see the moveend handler below), so the list only
   * refetches when the user actually asks it to.
   */
  onBoundsChange: (bounds: IMapBounds) => void;
  /** Fired when a pin is clicked (with its id) and when its popup closes
   * (with undefined). The popup itself is the only visual indicator of
   * which pin is selected -- callers that don't need to react to it can
   * pass a no-op. */
  onSelectItem: (itemId: string | undefined) => void;
  /**
   * Mobile only -- desktop leaves this unset and keeps the camera zooming to
   * exactly where the badge breaks apart. Fired with every item id inside a
   * cluster badge when it's clicked, so the caller can show the whole group
   * as a swipeable carousel instead. Zooming is no answer when a provider
   * lists many items at one address: those share a coordinate exactly, so no
   * zoom level ever separates them and the badge stays stuck at its count
   * forever.
   */
  onSelectCluster?: (itemIds: string[]) => void;
  /** Fired when the mini card inside a pin's popup is clicked -- navigates
   * to that item's full detail page. */
  onOpenItem: (itemId: string) => void;
  /** Card currently hovered in the list -- its pin grows/recolours on the map. */
  hoveredItemId?: string;
  /** Reports the pin the cursor is over, so the matching card can highlight. */
  onHoverItem: (itemId: string | undefined) => void;
  /**
   * Desktop/tablet only (never called when mobileControls is set -- see
   * ExploreMobileMap, which builds its own fixed card entirely outside this
   * component instead). Renders a clicked pin's own popup content -- the
   * caller looks `itemId` up in its own full item list (IMapPin alone has
   * no room for a title/photo) and returns the right category's compact
   * card, wired to call closePopup() before navigating away so the Azure
   * Maps popup itself doesn't linger open behind the new page.
   */
  renderPopupCard?: (itemId: string, closePopup: () => void) => React.ReactNode;
  /** Whether the panel around this map is currently expanded to fill the
   * space the results list otherwise takes -- purely a label/icon switch
   * here, the actual layout animation lives in the parent. */
  fullscreen?: boolean;
  /** Fired when the fullscreen toggle button is clicked. Omitted entirely
   * (rather than a no-op) hides the button -- some callers may not offer
   * this mode at all. */
  onToggleFullscreen?: () => void;
  /** Mobile /explore only: switches on the auto-debounced bounds search (no
   * "Search this area" button -- see the moveend handler below), compact
   * icon-only locate/satellite controls, and the redesigned pin popup.
   * Desktop/tablet leave this unset and keep the original manual-button,
   * text+icon-pill, renderPopupCard-driven popup behavior untouched. */
  mobileControls?: boolean;
  /** Extra top offset (px) for the compact locate/satellite control stack
   * (mobileControls only), so it clears the fixed app header docked above
   * the map. */
  topInset?: number;
  /** How much of the map's bottom (px, mobileControls only) the caller's own
   * UI currently covers -- ExploreMobileMap's SnapBottomSheet, whose 'media'
   * snap point (the default) sits over the bottom ~52% of the screen. The
   * results-fit effect below adds this on top of its own padding so a fitted
   * pin lands in the space actually *visible* above the sheet, not just
   * within the map container's full (partly hidden) bounds. */
  bottomInset?: number;
  /** Bumped by the caller every time a location search is actually
   * submitted (see HeavyDutyMachineries' locationSearchSeq) -- an explicit event
   * rather than a derived value, so searching the same place twice in a row
   * still re-triggers the fit-bounds effect below even though lat/lng come
   * back identical both times. Whenever this changes, that effect re-runs
   * even past its usual "only ever once" guard -- see its own comment for
   * why. */
  locationSearchSeq?: number;
  /** Whether the caller's own search request (the one that produced
   * `spaces`) is currently in flight -- distinct from this component's own
   * internal `loading` (whether the map SDK/tiles have finished loading).
   * locationSearchSeq bumps synchronously the moment a new location is
   * submitted, but `spaces` only catches up once that fetch resolves --
   * without this, the results-fit effect could see the new seq paired with
   * the *previous* search's spaces for a render or two and "spend" its
   * one-shot refit on stale data, right before the real results it should
   * have fitted to arrive. */
  resultsLoading?: boolean;
}

// Mobile-only auto-search (see the moveend handler below): fires
// onBoundsChange this long after the last pan/zoom settles, cancelling and
// restarting on every new movement in between -- so a continuous drag or a
// flurry of pinch-zooms only ever fires one request, right after the user
// actually stops.
const AUTO_SEARCH_DEBOUNCE_MS = 500;

// A touchscreen has no cursor, but the browser still synthesizes a mouse
// "hover" (mousemove) out of a tap, just before that same tap's own click --
// and Safari then withholds the click if anything on screen changed in
// response to that synthesized hover, so the tap is spent revealing the hover
// state and only a *second* tap activates what was touched. This map's own
// pin hover is exactly such a change (the tapped pill darkens, and the
// matching card grows in the results list), which is what made a pin on a
// real phone need two taps to open its card while the same view driven by a
// real mouse opened it on the first. Any hover arriving within this long
// after a touch is treated as that tap's own synthesized one and ignored --
// there's no cursor whose position it could be reporting anyway.
const TOUCH_SYNTHESIZED_HOVER_MS = 800;

// A click on a pin or a cluster badge also reaches the listeners watching for
// a click on the *background* -- same single native event, seen from two
// places. Each layer handler stamps the moment it fires, and anything within
// this long after that stamp is the same click still being delivered, not a
// new one somewhere else. A window (rather than a flag cleared afterwards)
// is what keeps this from depending on the order the listeners happen to run
// in for one event, which nothing guarantees.
const FEATURE_CLICK_GRACE_MS = 50;

const DATA_SOURCE_ID = 'spaces-source';

/**
 * Softens the base map so the price pills stay the loudest thing on it.
 * Azure Maps ships nine fixed styles and none of them is a pastel one --
 * anything softer than "road" in the catalogue is fully grey, which loses
 * the water and the road shields. Toning "road" down in the browser keeps
 * those and gets close to a Google-Maps-like palette.
 *
 * Applied to the canvas rather than the map container so the attribution
 * and the Microsoft logo, which are DOM siblings, keep their own colours.
 * Everything the map draws goes through this single canvas, pills
 * included -- they're white and near-black, so saturation doesn't touch
 * them, but the cluster bubbles do come out slightly softer. To retune:
 * lower "saturate" for more muted, raise "brightness" for more washed out.
 */
const MAP_CANVAS_FILTER = 'saturate(0.55) brightness(1.10)';
const DEFAULT_CENTER: [number, number] = [-3.7038, 40.4168]; // Madrid
const DEFAULT_ZOOM = 5;
const ROAD_STYLE = 'road';
const SATELLITE_STYLE = 'satellite_road_labels';

// A generic-city geocode (no street-level address) gives every space in
// that city the *exact same* coordinate. Azure's clustering groups points by
// on-screen pixel distance, so identical coordinates never separate no
// matter how far the guest zooms in -- the cluster badge would stay stuck at
// its count forever, hiding every space behind it but one.
//
// Nudging each duplicate a few metres off the shared point around a small
// circle fixes this using the SDK's own clustering, with no custom
// spiderfy/expand UI needed: a few metres is far fewer pixels than
// clusterRadius at a city-wide zoom (so they still merge into one badge from
// afar, same as before), but grows past it once the guest zooms in close
// enough -- exactly the point at which the badge should give way to
// individually clickable pins. The offset is a pure function of each space's
// own id and original coordinate, so it's stable across re-renders instead
// of jittering.
const DUPLICATE_SPREAD_METERS = 25;
const METERS_PER_DEGREE_LAT = 111320;

/** Groups spaces sharing the same rounded coordinate (~1m precision, enough
 * to catch a shared generic-city geocode without also catching two spaces
 * that merely happen to be very close together) and fans each group out in a
 * small circle around their shared point. */
const spreadCoincidentCoordinates = (items: IMapPin[]): Map<string, [number, number]> => {
  const groups = new Map<string, IMapPin[]>();
  items.forEach((item) => {
    if (item.latitude == null || item.longitude == null) return;
    const key = `${item.latitude.toFixed(5)},${item.longitude.toFixed(5)}`;
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  });

  const positions = new Map<string, [number, number]>();
  groups.forEach((group) => {
    if (group.length === 1) {
      const item = group[0];
      positions.set(item.id, [item.longitude as number, item.latitude as number]);
      return;
    }
    const originLat = group[0].latitude as number;
    const originLng = group[0].longitude as number;
    const metersPerDegreeLng = METERS_PER_DEGREE_LAT * Math.cos((originLat * Math.PI) / 180);
    group.forEach((item, i) => {
      const angle = (2 * Math.PI * i) / group.length;
      const lat = originLat + (DUPLICATE_SPREAD_METERS * Math.sin(angle)) / METERS_PER_DEGREE_LAT;
      const lng = originLng + (DUPLICATE_SPREAD_METERS * Math.cos(angle)) / metersPerDegreeLng;
      positions.set(item.id, [lng, lat]);
    });
  });
  return positions;
};

const toFeatures = (items: IMapPin[]): atlas.data.Feature<atlas.data.Point, Record<string, unknown>>[] => {
  const withCoords = items.filter((item) => item.latitude != null && item.longitude != null);
  const positions = spreadCoincidentCoordinates(withCoords);
  return withCoords.map((item) => {
    const position = positions.get(item.id) ?? [item.longitude as number, item.latitude as number];
    // "price" (a raw number, not the label) only ever feeds the cluster's
    // own minPrice aggregate (see clusterProperties below) -- parsed back
    // out of the label rather than carried separately, since every IMapPin
    // already guarantees a label but not the number that produced it.
    const numericPrice = parseFloat(item.priceLabel.replace(/[^0-9.,-]/g, '').replace(',', '.'));
    return new atlas.data.Feature(new atlas.data.Point(position), {
      spaceId: item.id,
      price: Number.isFinite(numericPrice) ? numericPrice : 0,
      priceLabel: item.priceLabel,
      liveStatus: item.liveStatus ?? 'Available',
    });
  });
};

// Drawn at 2x and rendered back down, so the baked-in text stays crisp on
// high-DPI screens: the sprite rasterises each SVG once, at the size given.
const PILL_SCALE = 2;
const PILL_HEIGHT = 28;
const PILL_FONT_SIZE = 13;
// Short labels like "30€" would otherwise come out almost circular.
const PILL_MIN_WIDTH = 52;

/**
 * Azure Maps can't put a background behind a SymbolLayer's text -- the SDK
 * exposes no icon-text-fit option, so a stretchable image would have
 * nothing to stretch to, and the price ends up as bare text on the map.
 * Baking the label into the icon is what gets an Airbnb-style pill. The
 * cost is one image per distinct price, which a page of results keeps to a
 * handful.
 */
// Only Rented/Maintenance get a ring at all -- Available (or no telemetry)
// keeps the exact same subtle hairline this pill always had, so the common
// case is visually untouched.
const LIVE_STATUS_RING_COLORS: Record<string, string> = {
  Rented: '#D89B3A',
  Maintenance: '#C24A3B',
};

const buildPricePill = (label: string, hovered: boolean, liveStatus: string): string => {
  const innerWidth = Math.max(PILL_MIN_WIDTH, label.length * 8 + 20) * PILL_SCALE;
  const innerHeight = PILL_HEIGHT * PILL_SCALE;
  // Room for the drop shadow, which would otherwise be clipped by the viewBox.
  const pad = 3 * PILL_SCALE;
  const width = innerWidth + pad * 2;
  const height = innerHeight + pad * 2;
  const background = hovered ? '#222222' : '#ffffff';
  const textColor = hovered ? '#ffffff' : '#222222';
  const ringColor = LIVE_STATUS_RING_COLORS[liveStatus];
  const strokeColor = ringColor ?? 'rgba(0,0,0,0.10)';
  const strokeWidth = (ringColor ? 2.5 : 1) * PILL_SCALE;

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<filter id="s" x="-30%" y="-30%" width="160%" height="160%">` +
    `<feDropShadow dx="0" dy="${PILL_SCALE}" stdDeviation="${PILL_SCALE}" flood-color="#000000" flood-opacity="0.22"/>` +
    `</filter>` +
    `<rect x="${pad}" y="${pad}" width="${innerWidth}" height="${innerHeight}" rx="${innerHeight / 2}"` +
    ` fill="${background}" stroke="${strokeColor}" stroke-width="${strokeWidth}" filter="url(#s)"/>` +
    `<text x="${width / 2}" y="${height / 2 + PILL_FONT_SIZE * PILL_SCALE * 0.35}" fill="${textColor}"` +
    ` font-family="system-ui, -apple-system, 'Segoe UI', sans-serif" font-size="${PILL_FONT_SIZE * PILL_SCALE}"` +
    ` font-weight="600" text-anchor="middle">${label}</text>` +
    `</svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const pillImageId = (label: string, hovered: boolean, liveStatus: string): string =>
  `price-pill-${hovered ? 'hover-' : ''}${liveStatus}-${label}`;

/** Picks the hovered pill for one space and the plain one for the rest --
 * each keyed by the feature's own liveStatus (see toFeatures) so a Rented/
 * Maintenance ring survives the hover-state swap too. */
const pillImageExpression = (hoveredSpaceId?: string): unknown => {
  const statusExpr = ['coalesce', ['get', 'liveStatus'], 'Available'];
  return hoveredSpaceId
    ? [
        'case',
        ['==', ['get', 'spaceId'], hoveredSpaceId],
        ['concat', 'price-pill-hover-', statusExpr, '-', ['get', 'priceLabel']],
        ['concat', 'price-pill-', statusExpr, '-', ['get', 'priceLabel']],
      ]
    : ['concat', 'price-pill-', statusExpr, '-', ['get', 'priceLabel']];
};

/**
 * Azure Maps hands a clicked *cluster* back as a raw GeoJSON Feature, not as
 * the atlas.Shape an individual point comes back as -- its own MapMouseEvent
 * doc says so outright: "Feature objects are not editable and either reside
 * in a VectorTileSource or represent a cluster point". A Feature carries
 * plain `.properties`/`.geometry`; only a Shape has getProperties()/
 * getCoordinates().
 *
 * This is the whole reason clicking a cluster badge used to do nothing at
 * all, silently: the handler read the cluster the same way the price-pin one
 * reads its pin, and getProperties simply isn't a function on a Feature --
 * the optional call swallowed it, cluster_id came back undefined, and the
 * handler returned before reaching any of its actual work. An `as
 * atlas.Shape` cast hid it from the compiler too, so nothing anywhere pointed
 * at it. Reading through either shape keeps both kinds of click honest.
 */
const readFeatureProperties = (target: unknown): Record<string, any> | undefined => {
  const candidate = target as { getProperties?: () => Record<string, any>; properties?: Record<string, any> } | undefined;
  return candidate?.getProperties?.() ?? candidate?.properties;
};

const readFeatureCoordinates = (target: unknown): atlas.data.Position | undefined => {
  const candidate = target as
    | { getCoordinates?: () => atlas.data.Position; geometry?: { coordinates?: atlas.data.Position } }
    | undefined;
  return candidate?.getCoordinates?.() ?? candidate?.geometry?.coordinates;
};

/**
 * Interactive Azure Maps view for /explore, following the SDK's official
 * "Point Clusters in Bubble Layer" pattern: one clustered DataSource feeding
 * a BubbleLayer (the groups) plus two SymbolLayers (the cluster count, and
 * the individual price pills).
 *
 * Authenticated with a Microsoft Entra token minted by our own backend --
 * the Azure Maps subscription key is shared with the other apps on this
 * auth server and never reaches the browser (see MapsService and
 * HeavyDutyMachineryApi's MapsController). When maps aren't configured on
 * the environment the token call 503s and this renders an inline notice
 * instead of breaking /explore.
 */
const SpacesMap = forwardRef<SpacesMapHandle, SpacesMapProps>(function SpacesMap(
  {
    items,
    onBoundsChange,
    onSelectItem,
    onSelectCluster,
    onOpenItem,
    hoveredItemId,
    onHoverItem,
    fullscreen,
    onToggleFullscreen,
    mobileControls,
    topInset,
    bottomInset,
    locationSearchSeq,
    resultsLoading,
    renderPopupCard,
  },
  ref
) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<atlas.Map | undefined>(undefined);
  useImperativeHandle(
    ref,
    () => ({
      getBounds: () => {
        const bounds = mapRef.current?.getCamera().bounds;
        if (!bounds) return undefined;
        // Azure Maps BoundingBox is [west, south, east, north] -- same
        // conversion as the moveend handler below.
        return { minLng: bounds[0], minLat: bounds[1], maxLng: bounds[2], maxLat: bounds[3] };
      },
    }),
    []
  );
  const dataSourceRef = useRef<atlas.source.DataSource | undefined>(undefined);
  const pricePillsRef = useRef<atlas.layer.SymbolLayer | undefined>(undefined);
  const hasFittedRef = useRef(false);
  // Tracks the locationSearchSeq a location search last actually fit the
  // camera to, so the same search re-running (e.g. a page/filter change
  // unrelated to location) doesn't re-yank the camera, but a genuinely new
  // submission -- even one that geocodes to the exact same place as the
  // last one -- does. See the results-fit effect below.
  const lastFocusedSeqRef = useRef<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [satellite, setSatellite] = useState(false);
  const [locating, setLocating] = useState(false);
  // The pin whose popup is currently open -- also folded into the pill's own
  // hover/selected colour switch (pillImageExpression) so the clicked pin
  // stays visually "active" while its mini card is showing.
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | undefined>(undefined);
  const popupRef = useRef<atlas.Popup | undefined>(undefined);
  // The popup's `content` option takes a plain DOM node -- this one is
  // created once and reused for every pin, with its actual React content
  // supplied via a portal (see the JSX below) so the mini card renders
  // through the normal tree and keeps the app's antd theme/i18n context,
  // rather than being mounted as a second, context-less React root.
  const popupContainerRef = useRef<HTMLDivElement | undefined>(undefined);
  // Timestamp of the last click that landed on a pin or a cluster badge --
  // read by both of the "clicked away from it" listeners below (the
  // document-wide one that closes desktop's popup, and the map-level one that
  // clears mobile's card) to tell that same click apart from a genuinely
  // different, later one on the background. See FEATURE_CLICK_GRACE_MS.
  const lastFeatureClickAtRef = useRef(0);
  // Timestamp of the last touch anywhere on the map -- see
  // TOUCH_SYNTHESIZED_HOVER_MS and the mousemove handler below.
  const lastTouchAtRef = useRef(0);
  // Latest items, read from inside the mount-only click handler below
  // (registered once) -- without this it would only ever see the very
  // first search's results.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // The initial auto-fit-to-results camera move (below) is not something the
  // user did -- reporting its bounds back would fire a second search for
  // (almost) the same results that were just fitted, which is exactly the
  // "loads twice" bug this was built to avoid. Set right before that one
  // setCamera call so the very next moveend it causes is swallowed.
  const suppressNextMoveRef = useRef(false);
  const pendingBoundsRef = useRef<IMapBounds | undefined>(undefined);
  const [hasPendingBounds, setHasPendingBounds] = useState(false);
  // mobileControls only -- see the moveend handler and AUTO_SEARCH_DEBOUNCE_MS.
  const autoSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Callers pass inline arrow functions, so keeping the latest of each in a
  // ref lets the map's own listeners stay registered once for the map's
  // whole lifetime instead of being torn down and re-added every render.
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onSelectSpaceRef = useRef(onSelectItem);
  const onSelectClusterRef = useRef(onSelectCluster);
  const onOpenSpaceRef = useRef(onOpenItem);
  const onHoverSpaceRef = useRef(onHoverItem);
  const renderPopupCardRef = useRef(renderPopupCard);
  // Same reasoning, but for a plain value rather than a callback: read at
  // fit time (see the results-fit effect below) rather than added to that
  // effect's own deps, so dragging the sheet on its own never re-triggers a
  // fit -- only an actual new search/data change does, same as everything
  // else that effect intentionally doesn't re-fit on.
  const bottomInsetRef = useRef(bottomInset);
  useEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
    onSelectSpaceRef.current = onSelectItem;
    onSelectClusterRef.current = onSelectCluster;
    onOpenSpaceRef.current = onOpenItem;
    onHoverSpaceRef.current = onHoverItem;
    renderPopupCardRef.current = renderPopupCard;
    bottomInsetRef.current = bottomInset;
  });

  useEffect(() => {
    let cancelled = false;
    let map: atlas.Map | undefined;

    const init = async () => {
      const mapsToken = await getMapsToken().catch(() => undefined);
      if (cancelled) return;
      if (!mapsToken || !containerRef.current) {
        setUnavailable(true);
        setLoading(false);
        return;
      }

      map = new atlas.Map(containerRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        language: 'es-ES',
        // "road" keeps the road shields and the water/park colouring that
        // make the map readable; MAP_CANVAS_FILTER is what tones it down.
        style: ROAD_STYLE,
        // Drops the Microsoft logo and the "Feedback"/copyright link the
        // SDK otherwise pins to the map's own corners.
        showLogo: false,
        showFeedbackLink: false,
        authOptions: {
          // "anonymous" is the SDK's name for Entra token auth -- it means
          // the SDK holds no credential of its own and asks getToken for a
          // bearer token whenever the current one is close to expiring.
          authType: atlas.AuthenticationType.anonymous,
          clientId: mapsToken.clientId,
          getToken: (resolve, reject) => {
            getMapsToken()
              .then((refreshed) => (refreshed ? resolve(refreshed.token) : reject(new Error('No map token'))))
              .catch(reject);
          },
        },
      });
      mapRef.current = map;

      map.events.add('ready', () => {
        if (cancelled || !map) return;

        const canvas = containerRef.current?.querySelector('canvas');
        if (canvas) (canvas as HTMLCanvasElement).style.filter = MAP_CANVAS_FILTER;

        const dataSource = new atlas.source.DataSource(DATA_SOURCE_ID, {
          cluster: true,
          clusterRadius: 45,
          clusterMaxZoom: 15,
          // Cheap enough to aggregate while clustering, and a "from 30€"
          // label is far more useful on a group than a bare count.
          clusterProperties: {
            minPrice: ['min', ['get', 'price']],
          },
        });
        map.sources.add(dataSource);
        dataSourceRef.current = dataSource;

        const clusterBubbles = new atlas.layer.BubbleLayer(dataSource, undefined, {
          filter: ['has', 'point_count'],
          radius: ['step', ['get', 'point_count'], 18, 10, 24, 50, 32],
          // Hardcoded rather than token.colorPrimary: this map isn't
          // rendered inside a coral-scoped ConfigProvider, so the token
          // would resolve to the app's black default instead of the brand
          // colour.
          color: CORAL,
          strokeColor: '#ffffff',
          strokeWidth: 2,
        });

        const clusterLabels = new atlas.layer.SymbolLayer(dataSource, undefined, {
          filter: ['has', 'point_count'],
          iconOptions: { image: 'none' },
          textOptions: {
            textField: ['get', 'point_count_abbreviated'],
            color: '#ffffff',
            font: ['SegoeUi-Bold'],
            size: 13,
            offset: [0, 0.1],
            allowOverlap: true,
          },
        });

        // Individual (unclustered) spaces render as a price pill. No
        // textOptions: the label is part of the icon (see buildPricePill).
        const pricePills = new atlas.layer.SymbolLayer(dataSource, undefined, {
          filter: ['!', ['has', 'point_count']],
          iconOptions: {
            image: pillImageExpression() as any,
            anchor: 'center',
            allowOverlap: true,
            size: 1 / PILL_SCALE,
          },
        });

        map.layers.add([clusterBubbles, clusterLabels, pricePills]);
        pricePillsRef.current = pricePills;

        // Clicking a cluster badge hands its own items up to be browsed as a
        // carousel where the caller asked for that (onSelectCluster, mobile),
        // and otherwise zooms exactly to where the badge breaks apart. Note
        // the cluster is read via readFeatureProperties, NOT the
        // getProperties() the price-pin handler below uses -- see that
        // helper's own comment for why that distinction is the whole ball
        // game here.
        map.events.add('click', clusterBubbles, (e: atlas.MapMouseEvent) => {
          // Stamped up front, well before the async work below resolves, so
          // the background-click listener can't mistake this same click for
          // one aimed at the map itself -- see FEATURE_CLICK_GRACE_MS. Only
          // on the path that opens something, though: on desktop a cluster
          // click just moves the camera, and an open popup being closed as it
          // does is the behaviour that was already there.
          if (mobileControls) lastFeatureClickAtRef.current = Date.now();
          const cluster = e.shapes?.[0];
          const properties = readFeatureProperties(cluster);
          const clusterId = properties?.cluster_id;
          const dataSource = dataSourceRef.current;
          if (clusterId == null || !dataSource || !map) return;

          const zoomToExpand = () => {
            dataSource.getClusterExpansionZoom(clusterId).then((zoom) => {
              const center = readFeatureCoordinates(cluster);
              if (center) map?.setCamera({ center, zoom, type: 'ease', duration: 300 });
            });
          };

          if (!onSelectClusterRef.current) {
            zoomToExpand();
            return;
          }

          // point_count is exactly how many the badge is standing in for, so
          // it's the natural limit -- a cluster's leaves are what the guest
          // is asking to see, all of them, not a page of them.
          dataSource.getClusterLeaves(clusterId, properties?.point_count ?? 0, 0).then((leaves) => {
            const itemIds = leaves
              .map((leaf) => readFeatureProperties(leaf)?.spaceId)
              .filter((id) => id != null)
              .map(String);
            // Falling back to the zoom keeps a badge from ever being a dead
            // tap, the exact failure this whole change exists to end.
            if (itemIds.length > 0) onSelectClusterRef.current?.(itemIds);
            else zoomToExpand();
          });
        });

        map.events.add('click', pricePills, (e: atlas.MapMouseEvent) => {
          lastFeatureClickAtRef.current = Date.now();
          const shape = e.shapes?.[0] as atlas.Shape | undefined;
          const spaceId = (shape as any)?.getProperties?.()?.spaceId;
          if (!spaceId || !map) return;

          // mobileControls: no Azure popup at all -- the selected space now
          // shows as a full-size card fixed above the bottom nav (see
          // ExploreMobileMap, reacting to the same onSelectSpace this
          // already fires below), not a small one anchored to the pin. A
          // pin-anchored popup made sense next to the compact card desktop
          // still uses; a screen-anchored card has no map position to
          // anchor *to*.
          if (mobileControls) {
            setSelectedSpaceId(String(spaceId));
            onSelectSpaceRef.current(String(spaceId));
            return;
          }

          if (!popupContainerRef.current) popupContainerRef.current = document.createElement('div');
          if (!popupRef.current) {
            // showPointer: false drops the pointer/arrow -- the card is
            // meant to read as a plain rectangle, not a speech bubble.
            // closeButton: false -- Azure Maps' own X sat right on top of
            // the card's price pill corner, and is redundant anyway: the
            // document-click-outside listener below already closes this
            // popup without it.
            const popup = new atlas.Popup({ pixelOffset: [0, -18], closeButton: false, showPointer: false });
            map.popups.add(popup);
            // Fires both for the popup's own close (X) button and for our
            // own explicit .close() calls below -- either way the pin is no
            // longer "selected", so clearing state here (instead of at each
            // call site) keeps the two ways of closing in sync.
            popup._addEventListener(
              'close',
              () => {
                setSelectedSpaceId(undefined);
                onSelectSpaceRef.current(undefined);
              },
              false
            );
            popupRef.current = popup;
          }
          // Flushed synchronously, before .open(): Azure Maps keeps its
          // popup inside the map's edges by measuring the *current*
          // rendered size of its content div right when it opens, but our
          // content is a React portal that React would otherwise only
          // paint on its own next (async) commit -- meaning that
          // measurement would run against last click's card, or nothing at
          // all on the very first one, and the edge-avoidance would use the
          // wrong size and could still let the popup hang off the map.
          flushSync(() => setSelectedSpaceId(String(spaceId)));
          popupRef.current.setOptions({ position: (shape as any).getCoordinates(), content: popupContainerRef.current });
          // Re-stamped after the flushSync above, not just at the top of this
          // handler: that synchronous render is the one thing here slow
          // enough to age the original stamp past its grace window and have
          // the document listener close the popup it just opened.
          lastFeatureClickAtRef.current = Date.now();
          popupRef.current.open(map);
          onSelectSpaceRef.current(String(spaceId));
        });

        // The canvas owns the cursor, and its own is the map's: grab, then
        // grabbing while dragging. Over a price pin that is a lie -- the pin
        // opens a machine, it does not drag anything. Looked up once here,
        // where the map is known to exist, rather than on every mousemove.
        const canvasContainer = map.getCanvasContainer();

        map.events.add('mousemove', pricePills, (e: atlas.MapMouseEvent) => {
          // Set before the touch gate below: a device with no pointer has no
          // cursor to get wrong, and the map puts its own back the moment a
          // drag starts.
          canvasContainer.style.cursor = 'pointer';

          // A tap's own synthesized hover, dropped so the browser doesn't
          // spend that tap showing it instead of delivering its click -- see
          // TOUCH_SYNTHESIZED_HOVER_MS.
          if (Date.now() - lastTouchAtRef.current < TOUCH_SYNTHESIZED_HOVER_MS) return;
          const shape = e.shapes?.[0] as atlas.Shape | undefined;
          const spaceId = (shape as any)?.getProperties?.()?.spaceId;
          onHoverSpaceRef.current(spaceId ? String(spaceId) : undefined);
        });
        // Deliberately not gated the same way: clearing a hover that a touch
        // never set is a no-op React bails out of, and leaving it ungated is
        // what keeps a *mouse* hover from surviving as a stale highlight on a
        // hybrid (touchscreen laptop) once the user switches to tapping.
        map.events.add('mouseleave', pricePills, () => {
          // Handed back to the map, which sets its own grab/grabbing from here.
          canvasContainer.style.cursor = '';
          onHoverSpaceRef.current(undefined);
        });

        // Tapping the map itself -- not a pin, not a cluster badge -- clears
        // the selection, so the card (or the carousel) can be dismissed by
        // tapping past it rather than having to hit its small "x". Bound
        // without a layer, so it sees every click on the map, feature clicks
        // included: the grace window is what tells those apart (see
        // FEATURE_CLICK_GRACE_MS).
        //
        // mobileControls only. Desktop's popup has had this since it was
        // built, through the document-level listener below -- which works by
        // hit-testing the popup's own DOM node, something mobile's card,
        // living outside this component entirely, was never in reach of.
        map.events.add('click', () => {
          if (!mobileControls) return;
          if (Date.now() - lastFeatureClickAtRef.current < FEATURE_CLICK_GRACE_MS) return;
          setSelectedSpaceId(undefined);
          onSelectSpaceRef.current(undefined);
        });

        map.events.add('moveend', () => {
          if (!map) return;
          if (suppressNextMoveRef.current) {
            suppressNextMoveRef.current = false;
            return;
          }
          const bounds = map.getCamera().bounds;
          if (!bounds) return;
          // Azure Maps BoundingBox is [west, south, east, north].
          const nextBounds: IMapBounds = {
            minLng: bounds[0],
            minLat: bounds[1],
            maxLng: bounds[2],
            maxLat: bounds[3],
          };
          if (mobileControls) {
            // Mobile has no "Search this area" button (see the render
            // below) -- panning/zooming searches on its own, debounced so a
            // continuous drag or a burst of pinch-zooms only fires once,
            // shortly after the gesture actually stops.
            if (autoSearchTimeoutRef.current) clearTimeout(autoSearchTimeoutRef.current);
            autoSearchTimeoutRef.current = setTimeout(() => {
              onBoundsChangeRef.current(nextBounds);
            }, AUTO_SEARCH_DEBOUNCE_MS);
          } else {
            // Desktop/tablet: held as "pending" instead of searched
            // immediately -- the user confirms via the "Search this area"
            // button (rendered below) so panning or zooming around doesn't
            // fire a request per move.
            pendingBoundsRef.current = nextBounds;
            setHasPendingBounds(true);
          }
        });

        setLoading(false);
      });
    };

    init();

    return () => {
      cancelled = true;
      if (autoSearchTimeoutRef.current) clearTimeout(autoSearchTimeoutRef.current);
      map?.dispose();
      mapRef.current = undefined;
      dataSourceRef.current = undefined;
    };
    // Mount-only: the map instance owns its own lifetime, data flows in via
    // the separate effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stamps every touch that reaches the map so the pin-hover handler above
  // can tell a real cursor from a tap's own synthesized one -- see
  // TOUCH_SYNTHESIZED_HOVER_MS. Registered in the capture phase, and on the
  // container rather than the canvas the SDK creates inside it, so the stamp
  // always lands before any of the SDK's own touch handling runs (and can't
  // be missed if that handling stops the event from bubbling back out).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const stamp = () => {
      lastTouchAtRef.current = Date.now();
    };
    const options: AddEventListenerOptions = { passive: true, capture: true };
    container.addEventListener('touchstart', stamp, options);
    container.addEventListener('touchend', stamp, options);
    return () => {
      container.removeEventListener('touchstart', stamp, options);
      container.removeEventListener('touchend', stamp, options);
    };
  }, []);

  // Clicking anywhere outside the popup's own content closes it, so the
  // user isn't forced to hit its X button. A plain `document` listener
  // rather than another Azure Maps map-click handler, since it doesn't
  // depend on Azure's own (per-layer) hit-testing to tell "clicked a pin"
  // from "clicked empty map" -- it just checks whether the click landed
  // inside the popup's DOM node. lastFeatureClickAtRef is what stops this
  // from immediately closing the very popup a pin click just opened/moved,
  // since that's also a "click outside the popup" as far as the DOM goes.
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (Date.now() - lastFeatureClickAtRef.current < FEATURE_CLICK_GRACE_MS) return;
      const popup = popupRef.current;
      if (!popup?.isOpen()) return;
      const target = e.target as Node | null;
      if (target && popupContainerRef.current?.contains(target)) return;
      popup.close();
    };
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  // Feed results in whenever they change, without recreating the map.
  useEffect(() => {
    const dataSource = dataSourceRef.current;
    if (!dataSource) return;
    const features = toFeatures(items);
    dataSource.clear();
    dataSource.add(features);

    // Register a pill image per distinct (price, live status) pair, in both
    // hover states. Adding an image the sprite already holds is a no-op, so
    // repeat searches over the same price points (and the common case of
    // every item being plain "Available") cost nothing extra.
    const sprite = mapRef.current?.imageSprite;
    if (sprite) {
      const labels = new Set(items.map((item) => item.priceLabel));
      const statuses = new Set(items.map((item) => item.liveStatus ?? 'Available'));
      labels.forEach((label) => {
        statuses.forEach((liveStatus) => {
          [false, true].forEach((hovered) => {
            const id = pillImageId(label, hovered, liveStatus);
            if (!sprite.hasImage(id)) sprite.add(id, buildPricePill(label, hovered, liveStatus));
          });
        });
      });
    }

    // Frame the results on the first batch that has any -- otherwise the map
    // would open on its default camera and the viewport filter would
    // immediately empty a list that does have results, just elsewhere. Past
    // that first time, re-fitting on every search would yank the camera back
    // and fight the user's own panning -- *except* when the guest just
    // submitted a location search (see locationSearchSeq below): that's
    // exactly the case where they DO want the camera to follow, e.g.
    // searching a place while the map is still sitting wherever it was left
    // from browsing earlier, same as any map-based search product.
    //
    // Gated on !resultsLoading, and lastFocusedSeqRef is only ever updated
    // right below, inside the actual fit -- not the moment locationSearchSeq
    // changes. locationSearchSeq bumps synchronously on submit, but `spaces`
    // (and therefore `features`) only catches up once that fetch resolves;
    // without both of those, this could "spend" a fresh seq on a render that
    // still holds the *previous* search's spaces (features.length > 0, just
    // the wrong ones), leaving nothing to trigger the real refit once the
    // actual results for the new search arrive a render later.
    //
    // Compared by an incrementing sequence number (bumped by the caller on
    // every location-search submit -- see HeavyDutyMachineries' locationSearchSeq)
    // rather than by the searched point itself: searching the exact same
    // place twice (e.g. wandering off after the first search, then typing
    // the same city again) geocodes to the identical lat/lng, which a
    // point-equality check would have mistaken for "already handled" and
    // silently skipped the second time.
    const isNewLocationSearch = locationSearchSeq != null && locationSearchSeq !== lastFocusedSeqRef.current;
    if (!resultsLoading && (!hasFittedRef.current || isNewLocationSearch) && features.length > 0 && mapRef.current) {
      hasFittedRef.current = true;
      lastFocusedSeqRef.current = locationSearchSeq;
      suppressNextMoveRef.current = true;
      // mobileControls' SnapBottomSheet can cover a good chunk of the map's
      // bottom (its default 'media' snap point sits over roughly half the
      // screen) -- padding the fit by that much extra keeps whatever gets
      // fitted above the sheet, in the space actually visible, rather than
      // technically "in view" of the map container but hidden behind it.
      const padding = { top: 60, bottom: 60 + (bottomInsetRef.current ?? 0), left: 60, right: 60 };
      mapRef.current.setCamera(
        isNewLocationSearch
          ? // An explicit "take me there" -- worth the pan-and-zoom animation
            // the plain initial/browsing fit below deliberately skips. Capped
            // (a little tighter than the browsing fit below, since this is a
            // deliberate single-place search) -- a *single* result is a
            // zero-size bounding box, which Azure Maps otherwise fits by
            // zooming in all the way to the map's own absolute max, cropping
            // the price pill against the edges with no breathing room.
            { bounds: atlas.data.BoundingBox.fromData(features), padding, maxZoom: 15, type: 'fly', duration: 1000 }
          : // Initial/browsing fit: capped so a first load with only one or
            // two nearby results doesn't zoom in uncomfortably tight.
            { bounds: atlas.data.BoundingBox.fromData(features), padding, maxZoom: 14 }
      );
    }
  }, [items, loading, locationSearchSeq, resultsLoading]);

  // A new search can drop the space whose card/popup is currently open --
  // it's no longer in view, or no longer matches the filters -- leaving it
  // open would show an empty shell since nothing renders for an id that
  // can't be found (the portal above on desktop, ExploreMobileMap's own
  // items.find on mobile). mobileControls has no popup instance to
  // .close() (see the click handler above) -- clear the state directly
  // instead, same as that popup's own 'close' listener already does for
  // desktop.
  //
  // Deliberately its own effect, separate from the results-feed one above:
  // that one rebuilds the entire dataSource (clear + re-add every feature,
  // which re-clusters) whenever it runs, and selectedSpaceId used to be one
  // of its dependencies purely for this same check -- meaning every single
  // pin tap re-triggered a full, unrelated data-source rebuild for no
  // reason, right as a click on the *next* pin needs the layer to be
  // reliably hit-testable. This effect only ever needs the cheap existence
  // check below, never the rebuild.
  useEffect(() => {
    if (selectedSpaceId && !items.some((i) => i.id === selectedSpaceId)) {
      if (mobileControls) {
        setSelectedSpaceId(undefined);
        onSelectSpaceRef.current(undefined);
      } else {
        popupRef.current?.close();
      }
    }
  }, [items, selectedSpaceId, mobileControls]);

  // The panel around this map changes width -- /explore's collapse toggle
  // slides it shut, the fullscreen toggle grows it, and the window itself
  // resizes. Azure Maps sizes its canvas when it's created and afterwards
  // only follows the window, so a container-level observer is what keeps
  // the canvas from staying stretched to a width the panel no longer has.
  //
  // Debounced on purpose: the panel's own width change is a CSS transition
  // (see HeavyDutyMachineries' MAP_SLIDE_MS), which fires a ResizeObserver tick on
  // nearly every animation frame while it runs. Calling Azure Maps' own
  // resize() on every one of those re-tiles the WebGL canvas at each
  // in-between size, which is exactly what flashed the map blank for a beat
  // mid-transition (most noticeable going fullscreen). The canvas element
  // itself still tracks the transition smoothly via plain CSS regardless
  // (it's width/height: 100% of its container) -- this only delays the
  // *map's* internal resize() until the container has stopped changing
  // size, so it fires once, right as the transition settles, instead of
  // dozens of times during it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      // A zero-width box means the panel is closed (or mid-animation):
      // resizing to it is wasted work and leaves the camera with no usable
      // bounds to report.
      if (!box || box.width < 1 || box.height < 1) return;
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        suppressNextMoveRef.current = true;
        mapRef.current?.resize();
      }, 150);
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, []);

  // Hovering a card in the list, or having a pin's popup open, flips the
  // matching pill to its dark state.
  useEffect(() => {
    const pricePills = pricePillsRef.current;
    if (!pricePills) return;
    pricePills.setOptions({
      iconOptions: {
        image: pillImageExpression(hoveredItemId ?? selectedSpaceId) as any,
        anchor: 'center',
        allowOverlap: true,
        size: 1 / PILL_SCALE,
      },
    });
  }, [hoveredItemId, selectedSpaceId, loading]);

  const handleSearchThisArea = () => {
    if (pendingBoundsRef.current) onBoundsChangeRef.current(pendingBoundsRef.current);
    setHasPendingBounds(false);
  };

  // Mobile-only "locate me" -- desktop's map has no equivalent control today
  // (its only geolocation entry point is HomeSearchBar's "near me" search,
  // unrelated to this map), so this is new rather than reused.
  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      message.error(translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_NEAR_ME_UNSUPPORTED));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        mapRef.current?.setCamera({
          center: [position.coords.longitude, position.coords.latitude],
          zoom: 13,
          type: 'ease',
          duration: 500,
        });
      },
      () => {
        setLocating(false);
        message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_LOCATE_ERROR));
      },
      { timeout: 10000 }
    );
  };

  const toggleSatellite = () => {
    const next = !satellite;
    const map = mapRef.current;
    if (!map) return;
    setSatellite(next);
    map.setStyle({ style: next ? SATELLITE_STYLE : ROAD_STYLE });
    // Swapping the canvas filter right here (synchronously) would apply it
    // to the *previous* style's tiles, since setStyle's own tile swap is
    // async -- that's what flashed the old style unfiltered for a frame
    // before the new one appeared. Waiting for 'styledata' (the new style
    // actually landing) makes the filter change land together with it.
    map.events.addOnce('styledata', () => {
      const canvas = containerRef.current?.querySelector('canvas');
      // MAP_CANVAS_FILTER is tuned for the flat "road" style's colours --
      // satellite imagery looks better left at its own natural tones.
      if (canvas) (canvas as HTMLCanvasElement).style.filter = next ? '' : MAP_CANVAS_FILTER;
    });
  };

  if (unavailable) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: token.colorFillTertiary,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_UNAVAILABLE)}</Text>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* Azure Maps' own popup radius (4px) reads as barely-rounded next to
          this app's pill-shaped buttons and rounded cards -- overridden here
          to match. The corner-specific rules also need overriding on top of
          the plain one: Azure squares off whichever corner sits nearest the
          popup's arrow so the pointer looks seamless, but with the pointer
          removed (showPointer: false, above) that corner would otherwise be
          the only sharp one on an all-round rounded card. */}
      <style>{`
        .atlas-map .popup-content-container {
          border-radius: 20px !important;
          padding: 0 !important;
        }
        .atlas-map .popup-container.top-left > .popup-content-container,
        .atlas-map .popup-container.top-right > .popup-content-container,
        .atlas-map .popup-container.bottom-left > .popup-content-container,
        .atlas-map .popup-container.bottom-right > .popup-content-container {
          border-radius: 20px !important;
        }
      `}</style>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: token.colorBgContainer,
          }}
        >
          <Spin />
        </div>
      )}
      {/* Desktop/tablet only -- mobile searches automatically as the camera
          settles (see the moveend handler above), so this manual-confirm
          button never applies there. */}
      {!loading && !mobileControls && hasPendingBounds && (
        <div style={{ position: 'absolute', top: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
          <Button
            shape="round"
            icon={<IconSearch size={15} />}
            onClick={handleSearchThisArea}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.24)' }}
          >
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_THIS_AREA_BUTTON)}
          </Button>
        </div>
      )}
      {/* Desktop/tablet only -- mobile's map is always full-bleed within its
          own bottom-sheet layout (see /explore's mobile branch), so there's
          nothing for this to toggle there; that caller simply never passes
          onToggleFullscreen. */}
      {!loading && !mobileControls && onToggleFullscreen && (
        <div style={{ position: 'absolute', bottom: 16, left: 16 }}>
          <Button
            shape="round"
            icon={fullscreen ? <IconArrowsMinimize size={15} /> : <IconArrowsMaximize size={15} />}
            onClick={onToggleFullscreen}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.24)' }}
          >
            {translate(
              t,
              fullscreen ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_EXIT_FULLSCREEN_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_FULLSCREEN_BUTTON
            )}
          </Button>
        </div>
      )}
      {/* Desktop/tablet: unchanged bottom-right satellite toggle. Mobile
          (mobileControls) gets its own top-right stack below instead --
          bottom-right on a phone sits under (or gets covered by) the
          draggable results sheet as soon as it rises past collapsed. */}
      {!loading && !mobileControls && (
        <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
          <Button
            shape="round"
            icon={<IconMap2 size={15} />}
            onClick={toggleSatellite}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.24)' }}
          >
            {translate(
              t,
              satellite ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_ROAD_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_SATELLITE_BUTTON
            )}
          </Button>
        </div>
      )}
      {/* Mobile: compact icon-only circles (no text) instead of desktop's
          text+icon pills, anchored top-right of the visible map area
          (topInset clears the fixed app header docked above -- see the
          caller). Top rather than bottom so the results sheet -- which can
          rise as far as 'full', covering almost the entire map -- never
          buries these; the sheet's own collapsed strip and floating "Map"
          button live at the bottom instead, so there's no overlap either
          way. Each button's own hit area is a full 44px square (the
          accessible touch-target minimum) even though the visible circle
          inside it is only ~40px, via padding rather than a bigger visible
          circle. */}
      {!loading && mobileControls && (
        <div
          style={{
            position: 'absolute',
            top: topInset ?? 16,
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            alignItems: 'flex-end',
          }}
        >
          <button
            type="button"
            aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_LOCATE_BUTTON)}
            onClick={handleLocateMe}
            disabled={locating}
            style={{ width: 44, height: 44, padding: 2, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: token.colorBgContainer,
                boxShadow: '0 2px 8px rgba(0,0,0,0.24)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {locating ? <Spin size="small" /> : <IconCurrentLocation size={18} />}
            </span>
          </button>
          <button
            type="button"
            aria-label={translate(
              t,
              satellite ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_ROAD_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_SATELLITE_BUTTON
            )}
            onClick={toggleSatellite}
            style={{ width: 44, height: 44, padding: 2, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: token.colorBgContainer,
                boxShadow: '0 2px 8px rgba(0,0,0,0.24)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconLayersIntersect size={18} />
            </span>
          </button>
        </div>
      )}
      {/* Desktop/tablet only -- mobileControls never opens this popup at
          all (see the click handler above), rendering its own full-size
          card fixed above the bottom nav instead (ExploreMobileMap), not
          one anchored to the pin. */}
      {selectedSpaceId &&
        popupContainerRef.current &&
        (() => {
          const content = renderPopupCardRef.current?.(selectedSpaceId, () => popupRef.current?.close());
          if (!content) return null;
          return createPortal(<div style={{ padding: 12 }}>{content}</div>, popupContainerRef.current);
        })()}
    </div>
  );
});

export default SpacesMap;
